import * as React from 'react';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { useAppSettings } from './AppSettings';
import { useInstrumentation } from './Instrumentation';
import { useAddNotification } from './Notifications';
import { useCredentialsForPermissionClaim } from './Permissions';
import { PermissionClaim } from '../code-generated/PermissionClaim.generated';
import { TimeoutLink } from '../utils/TimeoutLink';
import { createSigV4Fetch } from '../clients/createSigV4Fetch';
import { buildAwsTemporaryCredentialProvider } from '../clients/buildAwsTemporaryCredentialProvider';
import { getRoleArn } from '../utils/gsRoleUtils';
import { accountsHelper } from '../utils/accountsUtils';
import { useUsername } from './Midway';
import type { DependencyApiApolloClients } from './GsocApolloHooks';
import { DependencyApiApolloContext } from './GsocApolloHooks';
import { DependencyApi } from '../constants/api';
import type { GsocAwsTemporaryCredentialsFragment } from '../code-generated/curated.generated';
import type { AwsCredentialIdentityProvider } from '@aws-sdk/types';

import { REGION_TO_AIRPORT_CODE } from '../constants/regionAirportCodes';

const APPROVALS_ROLE_ARNS: Record<string, string> = {
  gamma: 'arn:aws:iam::475890425152:role/Approvals-420-AWS-Ground-Station-Gamma-Role',
  prod: 'arn:aws:iam::773220525832:role/Approvals-430-AWS-Groundstation-Prod-Role',
};

const APPROVALS_ENDPOINT_REGION = 'us-west-2';
const APPROVALS_ENDPOINTS: Record<string, string> = {
  gamma: 'https://7ikktxkctvehfdl7wajluxfjxy.appsync-api.us-west-2.amazonaws.com/graphql',
  prod: 'https://wy2sfyraajfixax3e4gs5eeju4.appsync-api.us-west-2.amazonaws.com/graphql',
};

function getAmazonApprovalsRoleArn(stage: string): string {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return APPROVALS_ROLE_ARNS[stage] ?? APPROVALS_ROLE_ARNS['gamma']!;
}

function getAmazonApprovalsEndpoint(stage: string): string {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return APPROVALS_ENDPOINTS[stage] ?? APPROVALS_ENDPOINTS['gamma']!;
}

/**
 * 	Apollo Client provider for the Amazon Approvals AppSync GraphQL API.
 * 	This is used for creating Amazon Approvals during the customer onboarding flows.
 */
export function AmazonApprovalsApolloProvider(props: { readonly children: React.ReactNode }) {
  const { region, stage } = useAppSettings();
  const addNotification = useAddNotification();
  const { rumClient } = useInstrumentation();

  const writeSourceCredentials = useCredentialsForPermissionClaim(
    PermissionClaim.CustomerOnboardingMutating,
  ) as GsocAwsTemporaryCredentialsFragment;

  const username = useUsername();

  const errorLink = React.useMemo(
    () =>
      onError(({ graphQLErrors, networkError, operation }) => {
        if (networkError) {
          if ('statusCode' in networkError && networkError.statusCode === 401) {
            return;
          }
          rumClient?.recordError(networkError);
          console.error(`[Network error]: ${networkError.message}`, networkError);
          addNotification({
            header: 'Network error',
            message: networkError.message,
            type: 'error',
          });
        } else {
          for (const graphqlError of graphQLErrors ?? []) {
            rumClient?.recordError(graphqlError);
            console.error(
              `[GraphQL error]: Operation: ${operation.operationName}, Message: ${graphqlError.message}, Location: ${String(graphqlError.locations)}, Path: ${String(graphqlError.path)}`,
            );
            addNotification({
              header: 'GraphQL error',
              message: graphqlError.message,
              type: 'error',
            });
          }
        }
      }),
    [rumClient, addNotification],
  );

  const retryLink = React.useMemo(
    () =>
      new RetryLink({
        delay: { initial: 300, max: Infinity, jitter: true },
        attempts: {
          max: 5,
          retryIf: (networkError: object, _operation) => {
            if ('statusCode' in networkError) {
              return [408, 435, 429, 502, 503, 504].includes((networkError as { statusCode: number }).statusCode);
            }
            return true;
          },
        },
      }),
    [],
  );

  const timeoutLink = React.useMemo(() => new TimeoutLink({ timeoutMs: 8000 }), []);

  const sharedLinks = React.useMemo(
    () => ApolloLink.from([errorLink, retryLink, timeoutLink]),
    [errorLink, retryLink, timeoutLink],
  );

  const approvalsEndpoint = getAmazonApprovalsEndpoint(stage);

  // Build the two-hop credential chain for Amazon Approvals:
  //   CustomerOnboardingMutating source creds
  //     → Ops@CustomerOnboardingMutating@{stage}@{airport}@AmazonApprovals (GSOC account)
  //       → Approvals-4XX-AWS-Ground*-{Stage}-Role (Approvals account)
  const credentialProvider = React.useMemo<AwsCredentialIdentityProvider>(() => {
    const airportCode = REGION_TO_AIRPORT_CODE[region];
    if (airportCode === undefined) {
      throw Error('Airport code lookup for Approvals role assumption failed.');
    }

    const opsConsoleAccountId = accountsHelper.getAccount('opsconsole', stage, region)?.account_id;
    if (opsConsoleAccountId === undefined) {
      throw Error('Account ID lookup for Approvals role assumption failed.');
    }

    const gsocRoleName = `Ops@CustomerOnboardingMutating@${stage}@${airportCode}@AmazonApprovals`;
    const gsocRoleArn = getRoleArn(opsConsoleAccountId, gsocRoleName);

    const approvalsRoleArn = getAmazonApprovalsRoleArn(stage);

    return buildAwsTemporaryCredentialProvider({
      roleArn: approvalsRoleArn,
      username,
      masterCredentials: buildAwsTemporaryCredentialProvider({
        roleArn: gsocRoleArn,
        username,
        masterCredentials: writeSourceCredentials,
        region,
      }),
      region,
    });
  }, [writeSourceCredentials, region, stage, username]);

  const createHttpLink = React.useCallback(() => {
    const sigV4Fetch = createSigV4Fetch(credentialProvider, APPROVALS_ENDPOINT_REGION);
    return new HttpLink({ uri: approvalsEndpoint, fetch: sigV4Fetch });
  }, [credentialProvider, approvalsEndpoint]);

  const clients = React.useMemo<DependencyApiApolloClients>(
    () => ({
      [DependencyApi.AMAZON_APPROVALS_API]: new ApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            Approval: { keyFields: ['id'] },
            ApprovalSearch: { keyFields: ['id'] },
          },
        }),
        link: ApolloLink.from([sharedLinks, createHttpLink()]),
        devtools: { enabled: true, name: DependencyApi.AMAZON_APPROVALS_API },
        defaultOptions: { query: { errorPolicy: 'all' }, mutate: { errorPolicy: 'all' } },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    clients[DependencyApi.AMAZON_APPROVALS_API].setLink(ApolloLink.from([sharedLinks, createHttpLink()]));
  }, [clients, sharedLinks, createHttpLink]);

  React.useEffect(() => {
    const intervalHandle = setInterval(() => clients[DependencyApi.AMAZON_APPROVALS_API].cache.gc(), 5 * 60 * 1000);
    return () => clearInterval(intervalHandle);
  }, [clients]);

  return <DependencyApiApolloContext.Provider value={clients}>{props.children}</DependencyApiApolloContext.Provider>;
}
